import { type IntegrationDefinition } from '@/types/integrations';

export const INTEGRATION_DEFINITIONS: IntegrationDefinition[] = [
  // Financial Systems
  {
    id: 'access-financials',
    name: 'Access Financials',
    category: 'financial',
    logo: 'https://preprodgo.accessacloud.com/content/images/icons/128/financials.png',
    description: 'Sync customers, invoices, payments, and financial data',
    requiresOAuth: false,
    configFields: [
      { key: 'serverUrl', label: 'Server URL', type: 'url', required: true, placeholder: 'https://server.example.com' },
      { key: 'database', label: 'Database Name', type: 'text', required: true, placeholder: 'AccessDB' },
      { key: 'username', label: 'Username', type: 'text', required: true },
      { key: 'password', label: 'Password', type: 'password', required: true },
    ],
  },
  {
    id: 'quickbooks',
    name: 'QuickBooks',
    category: 'financial',
    logo: '📗',
    description: 'Connect to QuickBooks for accounting and invoicing',
    requiresOAuth: false,
    configFields: [
      { key: 'clientId', label: 'Client ID', type: 'text', required: true },
      { key: 'clientSecret', label: 'Client Secret', type: 'password', required: true },
      { key: 'realmId', label: 'Realm ID', type: 'text', required: true },
    ],
  },
  {
    id: 'xero',
    name: 'Xero',
    category: 'financial',
    logo: '📘',
    description: 'Integrate with Xero accounting software',
    requiresOAuth: false,
    configFields: [
      { key: 'clientId', label: 'Client ID', type: 'text', required: true },
      { key: 'clientSecret', label: 'Client Secret', type: 'password', required: true },
    ],
  },
  {
    id: 'dimensions',
    name: 'Access Dimensions',
    category: 'financial',
    logo: 'https://provisioning-preproderp.accessacloud.com/Provisioning/images/DimensionsSaas.png',
    description: 'Connect to Dimensions for financial management and reporting',
    requiresOAuth: false,
    configFields: [
      { key: 'apiUrl', label: 'API URL', type: 'url', required: true, placeholder: 'https://api.dimensions.com' },
      { key: 'apiKey', label: 'API Key', type: 'password', required: true },
      { key: 'companyId', label: 'Company ID', type: 'text', required: true },
    ],
  },

  // Communication Platforms
  {
    id: 'outlook',
    name: 'Outlook',
    category: 'communication',
    logo: '📧',
    description: 'Send emails and manage communications via Outlook',
    requiresOAuth: true,
  },
  {
    id: 'gmail',
    name: 'Gmail',
    category: 'communication',
    logo: '✉️',
    description: 'Send emails and manage communications via Gmail',
    requiresOAuth: true,
  },
  {
    id: 'twilio',
    name: 'Twilio',
    category: 'communication',
    logo: '📱',
    description: 'Send SMS and make calls via Twilio',
    requiresOAuth: false,
    configFields: [
      { key: 'accountSid', label: 'Account SID', type: 'text', required: true },
      { key: 'authToken', label: 'Auth Token', type: 'password', required: true },
      { key: 'phoneNumber', label: 'Phone Number', type: 'text', required: true, placeholder: '+1234567890' },
    ],
  },

  // Credit Bureaus
  {
    id: 'experian',
    name: 'Experian',
    category: 'credit',
    logo: '📊',
    description: 'Access credit reports and scores from Experian',
    requiresOAuth: false,
    configFields: [
      { key: 'apiKey', label: 'API Key', type: 'password', required: true },
      { key: 'username', label: 'Username', type: 'text', required: true },
      { key: 'password', label: 'Password', type: 'password', required: true },
    ],
  },
  {
    id: 'equifax',
    name: 'Equifax',
    category: 'credit',
    logo: '📈',
    description: 'Access credit reports and scores from Equifax',
    requiresOAuth: false,
    configFields: [
      { key: 'apiKey', label: 'API Key', type: 'password', required: true },
      { key: 'memberId', label: 'Member ID', type: 'text', required: true },
      { key: 'securityCode', label: 'Security Code', type: 'password', required: true },
    ],
  },
  {
    id: 'transunion',
    name: 'TransUnion',
    category: 'credit',
    logo: '📉',
    description: 'Access credit reports and scores from TransUnion',
    requiresOAuth: false,
    configFields: [
      { key: 'apiKey', label: 'API Key', type: 'password', required: true },
      { key: 'subscriberId', label: 'Subscriber ID', type: 'text', required: true },
      { key: 'password', label: 'Password', type: 'password', required: true },
    ],
  },

  // Banking & Payment
  {
    id: 'stripe',
    name: 'Stripe',
    category: 'banking',
    logo: '💳',
    description: 'Process payments and manage subscriptions with Stripe',
    requiresOAuth: false,
    configFields: [
      { key: 'publishableKey', label: 'Publishable Key', type: 'text', required: true },
      { key: 'secretKey', label: 'Secret Key', type: 'password', required: true },
    ],
  },
  {
    id: 'paypal',
    name: 'PayPal',
    category: 'banking',
    logo: '💰',
    description: 'Accept payments via PayPal',
    requiresOAuth: false,
    configFields: [
      { key: 'clientId', label: 'Client ID', type: 'text', required: true },
      { key: 'clientSecret', label: 'Client Secret', type: 'password', required: true },
      { key: 'mode', label: 'Mode', type: 'select', required: true, options: [
        { label: 'Sandbox', value: 'sandbox' },
        { label: 'Live', value: 'live' },
      ]},
    ],
  },
  {
    id: 'plaid',
    name: 'Plaid',
    category: 'banking',
    logo: '🏦',
    description: 'Connect bank accounts and verify payments',
    requiresOAuth: false,
    configFields: [
      { key: 'clientId', label: 'Client ID', type: 'text', required: true },
      { key: 'secret', label: 'Secret', type: 'password', required: true },
      { key: 'environment', label: 'Environment', type: 'select', required: true, options: [
        { label: 'Sandbox', value: 'sandbox' },
        { label: 'Development', value: 'development' },
        { label: 'Production', value: 'production' },
      ]},
    ],
  },

  // Data & Analytics
  {
    id: 'tableau',
    name: 'Tableau',
    category: 'data',
    logo: '📊',
    description: 'Visualize data with Tableau analytics',
    requiresOAuth: false,
    configFields: [
      { key: 'serverUrl', label: 'Server URL', type: 'url', required: true, placeholder: 'https://tableau.example.com' },
      { key: 'siteName', label: 'Site Name', type: 'text', required: true },
      { key: 'username', label: 'Username', type: 'text', required: true },
      { key: 'password', label: 'Password', type: 'password', required: true },
    ],
  },
  {
    id: 'power-bi',
    name: 'Power BI',
    category: 'data',
    logo: '📈',
    description: 'Create reports and dashboards with Power BI',
    requiresOAuth: false,
    configFields: [
      { key: 'clientId', label: 'Client ID', type: 'text', required: true },
      { key: 'clientSecret', label: 'Client Secret', type: 'password', required: true },
      { key: 'tenantId', label: 'Tenant ID', type: 'text', required: true },
    ],
  },
  {
    id: 'google-analytics',
    name: 'Google Analytics',
    category: 'data',
    logo: '📉',
    description: 'Track and analyse user behaviour',
    requiresOAuth: false,
    configFields: [
      { key: 'trackingId', label: 'Tracking ID', type: 'text', required: true, placeholder: 'UA-XXXXXXXXX-X' },
      { key: 'propertyId', label: 'Property ID', type: 'text', required: true },
    ],
  },
];

export const CATEGORY_LABELS: Record<string, string> = {
  financial: 'Financial Systems',
  communication: 'Communication Platforms',
  credit: 'Credit Bureaus',
  banking: 'Banking & Payment',
  data: 'Data & Analytics',
};
